import { Sync } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  List,
  Typography,
  useTheme,
  Alert,
  Box,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  useGetAccountOnboardingStatusQuery,
  useGetAccountRelationshipQuery,
  useGetBuyerCreditLimitQuery,
  useGetCustomerTemsheetListQuery,
  useGetPartnersQuery,
} from "api/api-accounts";
import {
  useLazyGetConsentLetterEmailTemplateQuery,
} from "api/api-post-cc";
import {
  ADD_PARTNERS,
  BUYER,
  CL_APPROVED,
  CL_REJECTED,
  INCOMLEND_INTERNAL,
  PENDING_CUSTOMER_APPROVAL,
  REJECTED_BY_COUNTRY_MATRIX,
} from "codes";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { BookMeetingTabParam } from "helpers/bookMeetingConstants";
import { buildBookMeetingUrl } from "helpers/bookMeetingHelpers";
import { isRfPartnerCountryMatrixBlocked } from "components/Company/Lead/utils/reverseFactoringFlow";
import { useGetAccountByIdQuery } from "hooks/useGetAccountById";
import ToDoListing from "components/Company/ToDoListing";
import SignedPartner from "components/Onboard/Companies/Partner/SignedPartner";
import TermsheetToDo from "components/Termsheets/TermsheetToDo";
import ConsentLetterToDo from "./ConsentLetterToDo";
import BuyerRequestToDo from "./BuyerRequestToDo";
import { useGetMarketplaceAccountInvoicesQuery } from "api/api-marketplace";
import history from "helpers/history";
import useIsMobile from "hooks/useIsMobile";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "redux/hooks";

const DashBoardBody = () => {
  const USER_ROLE = useAppSelector((state) => state.appState.role);

  const isMobile = useIsMobile();
  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);

  const {
    data: company,
    isLoading: isLoadingCompany,
    isFetching: isFetchingCompany,
    refetch: refetchCompany,
  } = useGetAccountByIdQuery(USER_ROLE?.id, {
    skip: APP_PERMISSION === INCOMLEND_INTERNAL,
  });

  const {
    data: termsheetTodos,
    isFetching: isFetchingTermsheetTodos,
    refetch: refetchTermsheetTodos,
  } = useGetCustomerTemsheetListQuery(
    {
      termshetparams: `status=${PENDING_CUSTOMER_APPROVAL}`,
      accountId: USER_ROLE?.id || "",
    },
    {
      skip:
        !Boolean(USER_ROLE?.id) ||
        APP_PERMISSION === INCOMLEND_INTERNAL ||
        USER_ROLE?.id === INCOMLEND_INTERNAL,
    },
  );

  const {
    data: partners,
    isFetching,
    isUninitialized,
  } = useGetPartnersQuery(USER_ROLE.id ?? "", {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,

    skip: !Boolean(USER_ROLE.id),
  });
  const dashboardPartner = partners?.[0];
  const { data: dashboardRelationship } = useGetAccountRelationshipQuery(
    {
      companyId: USER_ROLE?.id ?? "",
      partnerId: dashboardPartner?.id ?? "",
    },
    { skip: !USER_ROLE?.id || !dashboardPartner?.id },
  );
  const requestedConsentLetterTemplateId = useRef<string | undefined>(
    undefined,
  );
  const [
    getConsentLetterEmailTemplate,
    {
      data: consentLetterEmailTemplate,
    },
  ] = useLazyGetConsentLetterEmailTemplateQuery();

  useEffect(() => {
    const accountRelationshipId = dashboardRelationship?.id;

    if (
      !accountRelationshipId ||
      requestedConsentLetterTemplateId.current === accountRelationshipId
    ) {
      return;
    }

    requestedConsentLetterTemplateId.current = accountRelationshipId;
    getConsentLetterEmailTemplate(
      { account_relationship_id: accountRelationshipId },
      true,
    );
  }, [dashboardRelationship?.id, getConsentLetterEmailTemplate]);

  const profile = useAppSelector((state) => state.appState.profile);
  const theme = useTheme();
  const { t } = useTranslation();
  const { refetch: refetchOnboardingStatus } =
    useGetAccountOnboardingStatusQuery(USER_ROLE?.id, {
      skip: !Boolean(USER_ROLE?.id),
    });

  const { data: buyerCreditLimit } = useGetBuyerCreditLimitQuery(
    USER_ROLE?.id ?? "",
    {
      skip:
        !Boolean(USER_ROLE?.id) ||
        profile !== BUYER.toLowerCase() ||
        APP_PERMISSION === INCOMLEND_INTERNAL ||
        USER_ROLE?.id === INCOMLEND_INTERNAL,
    },
  );

  const creditLimitStatus = company?.credit_limit?.status ?? "";
  const hasOwnClRow = Boolean(buyerCreditLimit?.id);
  const isCountryMatrixRejected = (creditLimit?: {
    status?: string;
    internal_status?: string;
  }) =>
    String(creditLimit?.status ?? "").toLowerCase() ===
      CL_REJECTED.toLowerCase() &&
    creditLimit?.internal_status === REJECTED_BY_COUNTRY_MATRIX;
  const isCLApproved =
    profile === BUYER.toLowerCase() &&
    Boolean(creditLimitStatus) &&
    String(creditLimitStatus).toLowerCase() === CL_APPROVED.toLowerCase();
  const hasPartners = (partners?.length ?? 0) > 0;
  const { data: marketplaceInvoices } = useGetMarketplaceAccountInvoicesQuery(
    USER_ROLE?.id ?? "",
    {
      skip:
        !USER_ROLE?.id ||
        APP_PERMISSION === INCOMLEND_INTERNAL ||
        USER_ROLE?.id === INCOMLEND_INTERNAL,
      refetchOnMountOrArgChange: true,
    },
  );
  const buyerRequestInvoices = (Array.isArray(marketplaceInvoices)
    ? marketplaceInvoices
    : marketplaceInvoices?.invoices ?? marketplaceInvoices?.data ?? []
  ).filter((invoice: any) => {
    const product = String(
      invoice?.product ??
        invoice?.account_product ??
        invoice?.map_product ??
        invoice?.map?.product ??
        "",
    )
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    const partnerProduct = partners?.find(
      (partner) =>
        partner?.id === (invoice?.partner_id ?? invoice?.partner?.id),
    )?.product;

    const isReverseFactoring =
      product === "reverse_factoring" ||
      String(partnerProduct ?? "").toLowerCase() === "reverse_factoring" ||
      (!product && profile === BUYER.toLowerCase());

    return isReverseFactoring && invoice?.buyer_request_sent === true;
  });
  const handleBookMeeting = () => {
    window.open(
      buildBookMeetingUrl({
        tab: BookMeetingTabParam.RISK_TEAM,
        accountId: USER_ROLE?.id,
      }),
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Grid container>
      <Grid item xs={12} lg={12} id="todo">
        <Typography
          fontWeight="bolder"
          fontSize={isMobile ? "1.3em" : "1.5em"}
          variant="h3"
          sx={{ mb: 2 }}
        >
          TO-DO
        </Typography>
      </Grid>
      {buyerRequestInvoices.length > 0 && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Paper
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "primary.light",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Buyer Request
            </Typography>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {buyerRequestInvoices.map((invoice: any) => (
                <BuyerRequestToDo
                  key={invoice?.invoice_id ?? invoice?.id}
                  invoices={[invoice]}
                />
              ))}
            </Box>
          </Paper>
        </Grid>
      )}
      {isCLApproved && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Alert
            severity="success"
            sx={{
              bgcolor:
                theme.palette.success.lighter ||
                `${theme.palette.success.main}14`,
              borderLeft: `4px solid ${theme.palette.success.main}`,
              "& .MuiAlert-message": { width: "100%" },
            }}
            action={
              !hasPartners ? (
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={() => history.push(ADD_PARTNERS)}
                >
                  {t("add-partner-prompt")}
                </Button>
              ) : undefined
            }
          >
            <Typography variant="body1" fontWeight="medium">
              {hasPartners
                ? t("profile-completed-partner-added")
                : t("credit-limit-approved-add-partners")}
            </Typography>
          </Alert>
        </Grid>
      )}
      {consentLetterEmailTemplate && dashboardRelationship?.id && (
        <Grid item xs={12}>
          <ConsentLetterToDo
            accountRelationshipId={dashboardRelationship.id}
            partnerName={dashboardPartner?.name}
            template={consentLetterEmailTemplate}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <List
          sx={{
            width: "100%",
            bgcolor: "background.paper",
          }}
          component="nav"
        >
          <TableContainer
            component={Paper}
            sx={{
              border: "1px solid rgb(224, 224, 224)",
              borderRadius: "10px",
              margin: "1vh auto",
              bgcolor: theme.palette.background.default,
            }}
          >
            <Table aria-label="to-do-table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="bold">My Company</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={refetchOnboardingStatus}>
                      <Sync />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2}>
                    {isFetchingCompany && <SkeletonLoad bars={1} />}
                    {!isFetchingCompany && company && (
                      <ToDoListing company={company} />
                    )}
                    {!isFetchingCompany &&
                      company &&
                      (() => {
                        const isDocumentReviewed =
                          (company?.document_reviewed_operation &&
                            company?.document_reviewed_risk) ||
                          false;
                        const callStatus = company?.call_status;
                        const canBookMeeting =
                          isDocumentReviewed &&
                          (callStatus === false || callStatus === null);

                        return canBookMeeting ? (
                          <Alert
                            severity="success"
                            sx={{
                              mt: 2,
                              bgcolor:
                                theme.palette.success.lighter || "#E8F5E9",
                              borderLeft: `4px solid ${theme.palette.success.main}`,
                              "& .MuiAlert-message": {
                                width: "100%",
                              },
                            }}
                          >
                            <Typography variant="body1" fontWeight="medium">
                              📞 You can now set up your call with the Incomlend
                              Review Team.
                            </Typography>
                          </Alert>
                        ) : null;
                      })()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer
            component={Paper}
            sx={{
              border: "1px solid rgb(224, 224, 224)",
              borderRadius: "10px",
              margin: "1vh auto",
              bgcolor: theme.palette.background.default,
            }}
          >
            <Table
              aria-label={`My ${
                profile === BUYER.toLowerCase() ? "Supplier" : "Buyer"
              }${partners && partners?.length > 1 ? "s" : ""}`}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="bold">{`My ${
                      profile === BUYER.toLowerCase() ? "Supplier" : "Buyer"
                    }${
                      partners && partners?.length > 1 ? "s" : ""
                    }`}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching && <SkeletonLoad bars={5} />}
                {!isFetching &&
                  Boolean(partners?.length) &&
                  partners?.map((partner, i) => {
                    const ts_signed = partner?.termsheetSigned;
                    const product = String(
                      (partner as { product?: string })?.product ?? "",
                    ).toLowerCase();
                    const isPartnerCountryMatrixRejected =
                      product === "reverse_factoring"
                        ? isRfPartnerCountryMatrixBlocked(partner)
                        : isCountryMatrixRejected(partner?.credit_limit);

                    // Book meeting logic for partners
                    const isDocumentReviewed =
                      (partner?.document_reviewed_operation &&
                        partner?.document_reviewed_risk) ||
                      false;
                    const callStatus = partner?.call_status;
                    const shouldShowBookMeeting = isDocumentReviewed;
                    const canBookMeeting =
                      isDocumentReviewed &&
                      (callStatus === false || callStatus === null);

                    return (
                      <TableRow key={partner?.id}>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <SignedPartner
                                partner={partner}
                                viewerOwnCreditLimit={
                                  String(
                                    (partner as { product?: string })
                                      ?.product ?? "",
                                  ).toLowerCase() === "reverse_factoring"
                                    ? buyerCreditLimit
                                    : undefined
                                }
                              />
                            </Box>
                            {isPartnerCountryMatrixRejected && (
                              <Alert
                                severity="error"
                                sx={{
                                  "& .MuiAlert-message": { width: "100%" },
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  sx={{ mb: 0.5 }}
                                >
                                  {t("rejected-by-country-matrix")}
                                </Typography>
                                <Typography variant="body2">
                                  {t("cl-country-matrix-rejection-onboarding")}
                                </Typography>
                              </Alert>
                            )}
                            {shouldShowBookMeeting && canBookMeeting && (
                              <Alert
                                severity="success"
                                sx={{
                                  bgcolor:
                                    theme.palette.success.lighter || "#E8F5E9",
                                  borderLeft: `4px solid ${theme.palette.success.main}`,
                                  "& .MuiAlert-message": {
                                    width: "100%",
                                  },
                                }}
                              >
                                <Typography variant="body2" fontWeight="medium">
                                  📞 You can now set up a call for{" "}
                                  {partner?.name || "this partner"} with the
                                  Incomlend Review Team
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          {isFetchingTermsheetTodos && <SkeletonLoad bars={5} />}
          {!isFetchingTermsheetTodos && termsheetTodos && (
            <TermsheetToDo
              termsheetTodos={termsheetTodos?.termsheets_details}
              syncAction={refetchTermsheetTodos}
            />
          )}
        </List>
      </Grid>
    </Grid>
  );
};
export default DashBoardBody;
